import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Payments as PaymentsIcon,
  UploadFile as UploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Navigation from '../../components/portal/Navigation';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMyPaymentDetails, uploadPaymentProof } from '../../store/slices/paymentSlice';
import { RootState } from '../../store';
import { ExamPaymentDetail, LessonPaymentDetail } from '../../types/portal';
import StatusBadge from '../../components/portal/common/StatusBadge';
import LessonCard from '../../components/portal/LessonCard';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 40px;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 32px;
  }
`;

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  padding: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 1.5rem;
`;

const TableContainer = styled.div`
  margin-bottom: 3rem;
`;



const List = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    border-top: 1px solid #e5e7eb;
  }
`;

const ListItem = styled.div`
  background: #ffffff;
  padding: 0.4rem 0.6rem;
  display: grid;
  grid-template-columns: 110px 90px 1fr 110px 90px;
  gap: 0.75rem;
  align-items: center;
  border: 1px solid #e5e7eb;
  margin-top: -1px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  @media (max-width: 1024px) {
    grid-template-columns: 90px 1fr 110px 90px;
    .hide-tablet { display: none; }
  }

  @media (max-width: 768px) {
    grid-template-columns: 65px 1fr auto;
    gap: 0.5rem;
    padding: 0.75rem 0.6rem;
    .hide-mobile { display: none; }
    .hide-tablet { display: none; }
  }

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-radius: 0 0 10px 10px;
  }

  .type-tag {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.15rem 0.45rem;
    border-radius: 6px;
    text-align: center;
    width: fit-content;
    text-transform: uppercase;
    
    @media (max-width: 768px) {
      font-size: 0.6rem;
      padding: 0.1rem 0.35rem;
    }
    
    &.lesson {
      background: #e0f2fe;
      color: #0369a1;
    }
    
    &.exam {
      background: #fef3c7;
      color: #92400e;
    }
  }

  .status-badge {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    text-align: center;
    width: fit-content;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-left: auto;

    &.booked {
      background: #ecfdf5;
      color: #059669;
      border: 1px solid #10b981;
    }
  }

  .amount {
    font-weight: 800;
    color: #111827;
    font-size: 0.85rem;
    text-align: right;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    text-align: left;

    .date-row {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-weight: 700;
      color: #111827;
      font-size: 0.8rem;

      @media (max-width: 768px) {
        font-size: 0.85rem;
      }
    }

    .time-part {
      @media (max-width: 768px) {
        display: none;
      }
    }
  }
`;

const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const SummaryCard = styled.div<{ $type?: 'total' | 'paid' | 'remaining' }>`
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    
    ${props => props.$type === 'total' && `
      background: #eff6ff;
      color: #3b82f6;
    `}
    ${props => props.$type === 'paid' && `
      background: #ecfdf5;
      color: #10b981;
    `}
    ${props => props.$type === 'remaining' && `
      background: #fff7ed;
      color: #f59e0b;
    `}

    svg {
      font-size: 24px;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    
    .label {
      font-size: var(--font-size-xs);
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .value {
      font-size: var(--font-size-h5);
      font-weight: 800;
      color: #1e293b;
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const TitleGroup = styled.div`
  h1 {
    font-size: var(--font-size-h1);
    font-weight: 800;
    color: var(--text-main);
    margin: 0;

    @media (max-width: 768px) {
      font-size: var(--font-size-h2);
    }
  }
  p {
    color: #6b7280;
    margin: 0.5rem 0 0 0;
    font-size: var(--font-size-p);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-weight: 700;
    font-size: 0.85rem;
    color: #374151;
    margin-bottom: 0.5rem;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid #d1d5db;
    font-size: 1rem;
    transition: border-color 0.2s;
    
    &:focus {
      outline: none;
      border-color: var(--primary-orange);
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
  color: white;
  font-weight: 800;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;

const RecordButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  border: none;
  background: var(--primary-orange);
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const StudentPaymentDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { paymentDetails, isLoading, uploadingProof } = useAppSelector((state: RootState) => state.payment);
  const { user } = useAppSelector((s) => s.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchMyPaymentDetails());
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !file) {
      setFormError('Please provide both amount and receipt.');
      return;
    }
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setFormError('Please provide a valid amount.');
      return;
    }

    try {
      const resultAction = await dispatch(uploadPaymentProof({ 
        file, 
        amount: numAmount, 
        description 
      }));
      
      if (uploadPaymentProof.fulfilled.match(resultAction)) {
        setIsModalOpen(false);
        setAmount('');
        setDescription('');
        setFile(null);
        setFormError(null);
        dispatch(fetchMyPaymentDetails());
      } else {
        setFormError(String(resultAction.payload || 'Failed to record payment.'));
      }
    } catch (err: any) {
      setFormError(err.message || 'An unexpected error occurred.');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}${t('portal.common.hours_short', 'h')} ${mins}${t('portal.common.minutes_short', 'm')}`;
    if (hours > 0) return `${hours}${t('portal.common.hours_short', 'h')}`;
    return `${mins}${t('portal.common.minutes_short', 'm')}`;
  };

  const combinedItems = React.useMemo(() => {
    if (!paymentDetails) return [];
    
    const lessons = (paymentDetails.lessons || []).map(l => ({
      ...l,
      displayType: 'lesson',
      sortDate: new Date(l.startTime).getTime()
    }));
    
    const exams = (paymentDetails.exams || []).map(e => ({
      ...e,
      displayType: 'exam',
      sortDate: new Date(e.startTime).getTime()
    }));
    
    return [...lessons, ...exams].sort((a, b) => b.sortDate - a.sortDate);
  }, [paymentDetails]);

  if (isLoading && !paymentDetails) {
    return (
      <PageWrapper>
        <Navigation />
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '3rem' }}>{t('portal.common.loading', 'Loading…')}</div>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (!paymentDetails) {
    return (
      <PageWrapper>
        <Navigation />
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '3rem' }}>{t('portal.student.dashboard.no_payments', 'No payment details available.')}</div>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        <HeaderSection>
          <TitleGroup>
            <h1>{t('nav.agenda', 'My Agenda')}</h1>
            <p>Track your upcoming lessons, exams, and payment status.</p>
          </TitleGroup>
          <RecordButton onClick={() => setIsModalOpen(true)}>
            <UploadIcon fontSize="small" />
            Record Payment
          </RecordButton>
        </HeaderSection>

        <ContentCard>
          <TableContainer>
            <SectionTitle>
              <PaymentsIcon /> {t('portal.student.payments.items_title', 'Lessons & Exams History')}
              <span style={{ marginLeft: 'auto', background: '#f3f4f6', color: '#6b7280', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 700 }}>
                {combinedItems.length} {t('portal.student.payments.items_label', 'Items')}
              </span>
            </SectionTitle>

            {(paymentDetails.pendingPayments || []).length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', marginBottom: '1rem' }}>Pending Proof Submissions</h4>
                <List>
                  {paymentDetails.pendingPayments.map((p: any) => (
                    <ListItem key={`pending-${p.id}`} style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
                      <div className="hide-tablet" style={{ fontSize: '0.85rem', color: '#4b5563', fontWeight: 600 }}>
                        {paymentDetails.studentName?.split(' ')[0]}
                      </div>
                      <div className="type-tag" style={{ background: '#fef3c7', color: '#d97706' }}>PAYMENT</div>
                      <div className="info">
                        <div style={{ fontWeight: 700 }}>{p.description || 'Recorded payment'}</div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {new Date(p.createdAt).toLocaleDateString()} • {p.proofDocument ? 'Receipt Attached' : 'No Receipt'}
                        </div>
                      </div>
                      <div className="hide-mobile"></div>
                      <div style={{ textAlign: 'right', fontWeight: 900, fontSize: '1.1rem', color: '#d97706' }}>
                        {formatCurrency(p.amount, paymentDetails.currency)}
                      </div>
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
            <List style={{ maxHeight: '800px', overflowY: 'auto' }}>
              {combinedItems.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', border: '1px solid #e5e7eb', borderRadius: '0 0 10px 10px', marginTop: '-1px' }}>
                  {t('portal.student.payments.no_items', 'No lessons or exams recorded.')}
                </div>
              ) : (
                combinedItems.map((item: any) => {
                  const isPassed = new Date(item.startTime) < new Date();
                  const itemForCard = {
                    id: item.id,
                    dateTime: item.startTime,
                    duration: item.duration,
                    location: item.location,
                    pickupLocation: item.pickupLocation,
                    status: item.status,
                    cost: item.cost,
                    currency: item.currency,
                    type: item.displayType,
                    studentName: item.studentName || paymentDetails.studentName
                  };
                  
                  return (
                    <LessonCard
                      key={`${item.displayType}-${item.id}`}
                      lesson={itemForCard}
                      mode={isPassed ? 'passed' : 'booked'}
                      showPrice={true}
                      onClick={() => {
                        if (item.displayType === 'exam') {
                          navigate(`/portal/student/exams/${item.id}`);
                        } else {
                          navigate(`/portal/lesson/${item.id}`);
                        }
                      }}
                    />
                  );
                })
              )}
            </List>
          </TableContainer>
        </ContentCard>

        <SummaryContainer>
          <SummaryCard $type="remaining">
            <div className="icon-wrapper">
              <TrendingUpIcon />
            </div>
            <div className="content">
              <span className="label">Remaining Balance</span>
              <span className="value">{formatCurrency(paymentDetails.totalDue - paymentDetails.totalPaid, paymentDetails.currency)}</span>
            </div>
          </SummaryCard>
          <SummaryCard $type="paid">
            <div className="icon-wrapper">
              <CheckCircleIcon />
            </div>
            <div className="content">
              <span className="label">Total Paid</span>
              <span className="value">{formatCurrency(paymentDetails.totalPaid, paymentDetails.currency)}</span>
            </div>
          </SummaryCard>
          <SummaryCard $type="total">
            <div className="icon-wrapper">
              <PaymentsIcon />
            </div>
            <div className="content">
              <span className="label">Total Billed</span>
              <span className="value">{formatCurrency(paymentDetails.totalDue, paymentDetails.currency)}</span>
            </div>
          </SummaryCard>
        </SummaryContainer>
      </ContentWrapper>

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', cursor: 'pointer', color: '#6b7280' }} onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </div>
            <ModalTitle>Record Payment</ModalTitle>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <label>Amount (EUR)</label>
                <input 
                  type="number" 
                  step="0.01" 
                  placeholder="0.00" 
                  value={amount} 
                  onChange={(e) => setAmount(e.target.value)}
                  required 
                />
              </FormGroup>
              <FormGroup>
                <label>Description (Optional)</label>
                <textarea 
                  rows={2} 
                  placeholder="e.g. Bank transfer on Friday" 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <label>Receipt (Image or PDF)</label>
                <input 
                  type="file" 
                  accept="image/*,application/pdf" 
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  required 
                />
              </FormGroup>
              
              {formError && (
                <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem', fontWeight: 600 }}>
                  ⚠️ {formError}
                </div>
              )}
              
              <SubmitButton type="submit" disabled={uploadingProof}>
                {uploadingProof ? 'Recording...' : 'Submit Payment Proof'}
              </SubmitButton>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

export default StudentPaymentDetailsPage;
